import { ArrowDown2 } from 'iconsax-react';
import { useEffect, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { useShallow } from 'zustand/react/shallow';
import { getUserId } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';
import cn from '@/utils/merge-class-names';

type Props = {
  isAtTailEdge: boolean;
  isScrolling?: boolean;
  onClick: () => void;
};

export default function ScrollToEndButton({ isAtTailEdge, isScrolling = false, onClick }: Props) {
  const lastMessageSeqIdRef = useRef<number | null>(null);
  const messages = useMessageStore(useShallow((s) => Array.from(s.messages.values())));
  const [unreadCount, setUnreadCount] = useDebounce(0, 1000);

  function handleClick() {
    setUnreadCount(0);
    onClick();
  }

  useEffect(() => {
    if (isAtTailEdge) {
      setUnreadCount(0);
      lastMessageSeqIdRef.current = null;
    } else {
      const userId = getUserId();
      if (!lastMessageSeqIdRef.current) {
        lastMessageSeqIdRef.current = Math.max(...messages.map((m) => m.SeqID ?? 0));
      }

      const deltaMessages = messages.filter((m) =>
        Boolean(m.UserID !== userId && m.SeqID && m.SeqID > lastMessageSeqIdRef.current!),
      );

      if (deltaMessages.length === 1 && deltaMessages.at(0)?.SeqID === lastMessageSeqIdRef.current) {
        setUnreadCount(0);
      } else {
        setUnreadCount(deltaMessages.length);
      }
    }
  }, [isAtTailEdge, messages]);

  return (
    <button
      type='button'
      disabled={isScrolling}
      className={cn(
        'indicator indicator-end indicator-top absolute -bottom-10 end-5 z-40 flex size-10 items-center justify-center gap-1.5 rounded-full bg-neutral-0 text-sm shadow-lg transition-all select-none',
        unreadCount > 0 ? 'text-primary' : 'text-neutral-950',
        { 'bottom-safe-offset-2': !isAtTailEdge || unreadCount > 0 },
        isScrolling && 'pointer-events-none',
      )}
      onClick={handleClick}>
      <ArrowDown2 className='size-6' />

      {unreadCount > 0 && (
        <span className='badge indicator-item badge-primary badge-sm aspect-square'>{unreadCount}</span>
      )}
    </button>
  );
}
